<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { messageTimestamp, formatDuration } from 'shared/helpers/timeHelper';
import RedhostingMeetAPI from 'dashboard/api/redhostingMeet';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import EmptyStateLayout from 'dashboard/components-next/EmptyStateLayout.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const RESULTS_PER_PAGE = 25;
const DATE_FORMAT = 'dd/MM/yyyy HH:mm';

const { t } = useI18n();

const meetings = ref([]);
const totalCount = ref(0);
const currentPage = ref(1);
const isFetching = ref(true);
const closingId = ref(null);

const fetchMeetings = async () => {
  isFetching.value = true;
  try {
    const { data } = await RedhostingMeetAPI.getMeetings(currentPage.value);
    meetings.value = data.payload;
    totalCount.value = data.meta.count;
  } catch (error) {
    useAlert(t('CALLS_PAGE.EMPTY_STATE'));
  } finally {
    isFetching.value = false;
  }
};

const onPageChange = page => {
  currentPage.value = page;
  fetchMeetings();
};

// Encerrar derruba quem estiver na sala; a duracao chega depois pelo Meet.
const closeMeeting = async meeting => {
  closingId.value = meeting.id;
  try {
    const { data } = await RedhostingMeetAPI.closeMeeting(meeting.id);
    meetings.value = meetings.value.map(item =>
      item.id === data.id ? data : item
    );
    useAlert(t('CALLS_PAGE.MEETINGS.CLOSE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CALLS_PAGE.MEETINGS.CLOSE_ERROR')
    );
  } finally {
    closingId.value = null;
  }
};

const participantNames = meeting =>
  meeting.participants.map(participant => participant.name).join(', ');

onMounted(fetchMeetings);
</script>

<template>
  <div
    v-if="isFetching && !meetings.length"
    class="flex items-center justify-center w-full h-full bg-n-surface-1"
  >
    <Spinner :size="24" />
  </div>
  <EmptyStateLayout
    v-else-if="!meetings.length"
    :title="t('CALLS_PAGE.MEETINGS.EMPTY_TITLE')"
    :subtitle="t('CALLS_PAGE.MEETINGS.EMPTY_SUBTITLE')"
    :show-backdrop="false"
  />
  <section
    v-else
    class="flex flex-col w-full h-full overflow-hidden bg-n-surface-1"
  >
    <header class="px-6 pt-6 pb-4 border-b shrink-0 border-n-weak">
      <h1 class="m-0 text-xl font-medium text-n-slate-12">
        {{ t('CALLS_PAGE.MEETINGS.HEADER') }}
      </h1>
      <p class="mt-1 mb-0 text-sm text-n-slate-11">
        {{ t('CALLS_PAGE.MEETINGS.SUBTITLE') }}
      </p>
    </header>
    <main class="flex-1 px-6 overflow-y-auto">
      <div
        v-for="meeting in meetings"
        :key="meeting.id"
        class="flex flex-wrap items-center gap-x-6 gap-y-2 py-4 border-b border-n-weak"
      >
        <div class="flex items-center flex-1 min-w-[14rem] gap-3">
          <Avatar
            :src="meeting.contact.thumbnail"
            :name="meeting.contact.name"
            :size="36"
            rounded-full
          />
          <div class="min-w-0">
            <div class="text-sm font-medium truncate text-n-slate-12">
              {{ meeting.contact.name }}
            </div>
            <div
              class="flex items-center gap-1.5 text-xs truncate text-n-slate-11"
            >
              <ChannelIcon :inbox="meeting.inbox" class="size-3.5" />
              <span class="truncate">{{ meeting.inbox.name }}</span>
            </div>
          </div>
        </div>
        <div class="w-40 min-w-0 text-sm">
          <div class="text-xs text-n-slate-10">
            {{ t('CALLS_PAGE.MEETINGS.COLUMNS.AGENT') }}
          </div>
          <div class="truncate text-n-slate-12">
            {{ meeting.agent?.name || '—' }}
          </div>
        </div>
        <div class="w-36 text-sm">
          <div class="text-xs text-n-slate-10">
            {{ t('CALLS_PAGE.MEETINGS.COLUMNS.WHEN') }}
          </div>
          <div class="text-n-slate-12 tabular-nums">
            {{ messageTimestamp(meeting.created_at, DATE_FORMAT) }}
          </div>
        </div>
        <div class="w-48 min-w-0 text-sm">
          <div class="text-xs text-n-slate-10">
            {{ t('CALLS_PAGE.MEETINGS.COLUMNS.DURATION') }}
          </div>
          <div
            v-if="meeting.started_at"
            v-tooltip.top="participantNames(meeting)"
            class="truncate text-n-slate-12"
          >
            <span class="tabular-nums">
              {{ formatDuration(meeting.duration_seconds) }}
            </span>
            <span class="text-n-slate-11">
              {{ ` · ${participantNames(meeting)}` }}
            </span>
          </div>
          <div v-else class="text-n-slate-10">
            {{ t('CALLS_PAGE.MEETINGS.NOT_STARTED') }}
          </div>
        </div>
        <div class="flex items-center gap-2 ms-auto">
          <router-link
            :to="{
              name: 'inbox_conversation',
              params: { conversation_id: meeting.conversation.display_id },
            }"
            class="inline-flex items-center h-7 gap-1 px-2 text-xs rounded-md outline outline-1 -outline-offset-1 outline-n-weak text-n-slate-11 hover:bg-n-alpha-1"
          >
            <Icon icon="i-lucide-message-circle" class="size-3.5" />
            {{ meeting.conversation.display_id }}
          </router-link>
          <span
            v-if="meeting.closed_at"
            class="inline-flex items-center h-7 gap-1 px-2 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
          >
            <Icon icon="i-lucide-circle-check" class="size-3.5" />
            {{ t('CALLS_PAGE.MEETINGS.CLOSED') }}
          </span>
          <template v-else>
            <Button
              :label="t('CALLS_PAGE.MEETINGS.CLOSE')"
              icon="i-lucide-phone-off"
              ruby
              faded
              xs
              :is-loading="closingId === meeting.id"
              @click="closeMeeting(meeting)"
            />
            <a
              :href="meeting.url"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center h-7 gap-1 px-2.5 text-xs font-medium text-white rounded-md bg-n-brand hover:bg-n-blue-10"
            >
              <Icon icon="i-lucide-video" class="size-3.5" />
              {{ t('CALLS_PAGE.MEETINGS.JOIN') }}
            </a>
          </template>
        </div>
      </div>
    </main>
    <footer class="sticky bottom-0 shrink-0">
      <PaginationFooter
        :current-page="currentPage"
        :total-items="totalCount"
        :items-per-page="RESULTS_PER_PAGE"
        @update:current-page="onPageChange"
      />
    </footer>
  </section>
</template>
